import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, ChevronDown, ChevronRight, Ship } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'new', label: 'Nouveau', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
  { value: 'contacte', label: 'Contacté', cls: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50' },
  { value: 'signe', label: 'Signé', cls: 'bg-green-500/20 text-green-300 border-green-500/50' },
  { value: 'perdu', label: 'Perdu', cls: 'bg-red-500/20 text-red-300 border-red-500/50' },
];

const TransmettreAdmin = ({ token }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/quote-request/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erreur chargement demandes de transmission');
      const all = await res.json();
      setLeads(all.filter((d) => d.service === 'transmettre-bateau'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchLeads();
  }, [token, fetchLeads]);

  const updateStatus = async (lead, status) => {
    try {
      const res = await fetch(`${API_URL}/api/quote-request/${lead.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Erreur mise à jour statut');
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    } catch (err) {
      alert(err.message);
    }
  };

  const filtered = leads.filter((l) => statusFilter === 'all' || (l.status || 'new') === statusFilter);
  const newCount = leads.filter((l) => (l.status || 'new') === 'new').length;

  return (
    <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl overflow-hidden mb-8" data-testid="transmettre-admin">
      <div className="p-4 border-b border-[#3f4816] flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Ship className="text-[#d9fb06]" size={22} />
          <div>
            <h2 className="text-lg font-bold text-[#d9fb06]">Transmission de bateau ({leads.length})</h2>
            <p className="text-xs text-[#888680] mt-1">{newCount} nouvelle(s) demande(s) · page /transmettre-son-bateau</p>
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-3 py-2 text-sm text-[#dfddd6]"
          data-testid="transmettre-admin-filter"
        >
          <option value="all">Tous les statuts</option>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center text-[#888680]"><Loader2 size={20} className="animate-spin mr-2" /> Chargement…</div>
      ) : error ? (
        <div className="p-8 text-center text-red-400">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center text-[#888680]" data-testid="transmettre-admin-empty">Aucune demande de transmission pour l'instant.</div>
      ) : (
        <div className="divide-y divide-[#3f4816]">
          {filtered.map((lead) => {
            const isOpen = expandedId === lead.id;
            const meta = STATUS_OPTIONS.find((s) => s.value === (lead.status || 'new')) || STATUS_OPTIONS[0];
            return (
              <div key={lead.id} data-testid={`transmettre-row-${lead.id}`}>
                <div className="p-4 flex items-center gap-3 hover:bg-[#3f4816]/20 transition-colors">
                  <button type="button" onClick={() => setExpandedId(isOpen ? null : lead.id)} className="text-[#888680] hover:text-[#d9fb06]" data-testid={`transmettre-toggle-${lead.id}`}>
                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <span className="text-[#dfddd6] font-semibold truncate">{lead.nom}</span>
                    {lead.zone && <span className="text-[#888680] text-sm"> — {lead.zone}</span>}
                    <p className="text-[#888680] text-xs mt-1">
                      {lead.created_at?.slice(0, 16).replace('T', ' ')} · {lead.email}{lead.telephone ? ` · ${lead.telephone}` : ''}
                    </p>
                  </div>
                  <select
                    value={lead.status || 'new'}
                    onChange={(e) => updateStatus(lead, e.target.value)}
                    className={`px-2 py-1 rounded-lg text-xs font-bold border ${meta.cls} bg-transparent`}
                    data-testid={`transmettre-status-${lead.id}`}
                  >
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-[#1a1c1b]">{o.label}</option>)}
                  </select>
                </div>
                {isOpen && (
                  <div className="px-4 pb-4 pl-12 text-sm text-[#dfddd6] space-y-1">
                    {lead.message && <p className="whitespace-pre-wrap">{lead.message}</p>}
                    <p className="text-xs text-[#888680] mt-2">ID : {lead.id}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TransmettreAdmin;
